import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SearchOffers from "./components/SearchOffers";
import OffersList from "./components/OffersList";
import { supabase } from "../../lib/supabase";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import { useFavorites } from "../../contexts/FavoritesContext";
import { userService } from "../../services/userService";
import SEO from "../../components/seo/SEO";

const normalizeImage = (img) => {
  if (!img) return "/assets/images/no_image.png";
  if (img.startsWith("http")) return img;
  if (img.startsWith("/")) return img;
  return "/" + img;
};

const OffersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { favorites } = useFavorites();

  const [activeTab, setActiveTab] = useState("search");
  const [searchResults, setSearchResults] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedSearchIds, setSelectedSearchIds] = useState([]);

  // Auto-fill states for resumed searches
  const [initialFormData, setInitialFormData] = useState(null);
  const [initialOfferType, setInitialOfferType] = useState(null);

  const resultsRef = useRef(null);
  const searchTopRef = useRef(null);

  // ✅ AICI, o singură dată
  useEffect(() => {
    loadSavedSearches();
  }, []);


  /* ===============================
      LOAD / SAVE / DELETE SEARCHES
  ================================ */
  const loadSavedSearches = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("saved_searches")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Adaptare date: decodăm metadata din description dacă e cazul
      const validData = (data || []).map(item => {
        try {
          // Încercăm să vedem dacă description conține payload (JSON)
          const parsed = JSON.parse(item.description);
          return {
            ...item,
            query: parsed.query || item.destination || "Search",
            offer_type: parsed.offerType || "hotel",
            payload: parsed.payload || {}
          };
        } catch (e) {
          // Fallback for old data or simple format
          return {
            ...item,
            query: item.description || item.destination || "Search",
            offer_type: "hotel",
            payload: { destination: item.destination }
          };
        }
      });

      setSavedSearches(validData);
    } catch (err) {
      console.error("Error loading searches:", err);
    }
  };

  const saveSearch = async (query, offerType, payload) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Adaptare pentru schema existentă:
      // Folosim 'description' ca stocare JSON pentru a nu pierde datele complexe
      const adaptedPayload = {
        user_id: user.id,
        name: `${offerType.toUpperCase()}: ${query.slice(0, 30)}`,
        destination: payload?.destination || query.slice(0, 50),
        description: JSON.stringify({ query, offerType, payload }), // Stocăm tot obiectul aici
        status: 'active'
      };

      const { error: insertError } = await supabase.from("saved_searches").insert(adaptedPayload);

      if (insertError) throw insertError;

      // FIFO Limit: 20
      const { data: all } = await supabase
        .from("saved_searches")
        .select("id")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (all && all.length > 20) {
        const idsToDelete = all.slice(20).map(x => x.id);
        await supabase.from("saved_searches").delete().in("id", idsToDelete);
      }

      await loadSavedSearches();
    } catch (err) {
      console.error("Error saving search:", err);
    }
  };

  const deleteSelectedSearches = async () => {
    if (selectedSearchIds.length === 0) return;
    try {
      const { error } = await supabase
        .from("saved_searches")
        .delete()
        .in("id", selectedSearchIds);

      if (error) throw error;

      setSavedSearches(prev => prev.filter(s => !selectedSearchIds.includes(s.id)));
      setSelectedSearchIds([]);
    } catch (err) {
      alert("Error during deletion");
    }
  };

  const deleteAllSearches = async () => {
    if (!window.confirm("Are you sure you want to delete ALL saved searches?")) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    try {
      const { error } = await supabase
        .from("saved_searches")
        .delete()
        .eq("user_id", user.id);

      if (error) throw error;
      setSavedSearches([]);
      setSelectedSearchIds([]);
    } catch (err) {
      alert("Error during deletion");
    }
  };

  /* ===============================
        SEARCH HANDLER
  ================================ */
  const handleSearch = async (query, offerType, payload, isAuto = false) => {
    setLoading(true);
    setActiveTab("search");
    setInitialFormData(null); // Reset pre-fill
    setInitialOfferType(null);
    // 🔥 SCROLL IMEDIAT DUPĂ CLICK
    requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            prompt: query,
            offerType,
            payload,
            user_id: session?.user?.id || null,
            session_id: userService.getSessionId(),
            client_path: `${location.pathname}${location.search}${location.hash}`,
            source: "offersSearch",
          }),
        }
      );

      const data = await response.json();
      const normalized = (data?.cards || []).map((o) => ({
        ...o,
        image: normalizeImage(o.image || o.image_url || o.imageUrl || o.thumbnail || o.photo || null),
        offer_id: o.offer_id || o.id || o.cta?.url || `${o.provider}-${o.title}`,
      }));

      setSearchResults(normalized);
      setHasSearched(true);

      // Automatic save if NOT a resumed search
      if (!isAuto && session?.user) {
        await saveSearch(query, offerType, payload);
      }
    } catch (err) {
      console.error("Search error", err);
      alert("Search error");
    } finally {
      setLoading(false);
    }
  };

  /* ===============================
      AUTO-RESUME FROM STATE
  ================================ */
  useEffect(() => {
    if (location.state?.autoSearch && location.state?.payload) {
      const { query, offerType, payload } = location.state;
      setInitialOfferType(offerType);
      setInitialFormData(payload);
      handleSearch(query, offerType, payload, true);
    }
  }, [location.state]);

  const toggleSelect = (id) => {
    setSelectedSearchIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  /* ===============================
        TAB CONTENT
  ================================ */
  const renderContent = () => {
    if (activeTab === "search") {
      return (
        <OffersList
          offers={searchResults}
          hasSearched={hasSearched}
          loading={loading}
          context="search"
        />
      );
    }

    if (activeTab === "favorites") {
      return (
        <OffersList
          offers={favorites.map((o) => ({
            ...o,
            image: normalizeImage(o.image),
          }))}
          hasSearched={true}
          loading={false}
          context="favorites"
        />
      );
    }

    if (activeTab === "searches") {
      return (
        <div className="space-y-4">

          {/* ACTION BAR */}
          <div className="bg-white border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <span className="text-sm text-slate-600">
              {selectedSearchIds.length} selected
            </span>

            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="text-rose-600 border-rose-300"
                disabled={selectedSearchIds.length === 0}
                onClick={deleteSelectedSearches}
                iconName="Trash2"
              >
                Delete selected
              </Button>

              <Button
                size="sm"
                variant="ghost"
                className="text-slate-500"
                onClick={deleteAllSearches}
              >
                Delete all
              </Button>
            </div>
          </div>

          {/* LIST */}
          {savedSearches.length === 0 ? (
            <div className="text-center py-16 text-slate-400 border rounded-lg bg-white">
              <Icon name="History" size={40} className="mx-auto mb-3 opacity-30" />
              <p>You don't have any saved searches yet.</p>
            </div>
          ) : (
            <div className="divide-y border rounded-lg bg-white">
              {savedSearches.map((s) => (
                <div
                  key={s.id}
                  className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3
                ${selectedSearchIds.includes(s.id) ? "bg-blue-50" : ""}
              `}
                >
                  {/* LEFT */}
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={selectedSearchIds.includes(s.id)}
                      onChange={() => toggleSelect(s.id)}
                    />

                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                          {s.offer_type}
                        </span>
                        <span className="font-medium text-slate-900 truncate">
                          {s.query}
                        </span>
                      </div>

                      <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                        <Icon name="Calendar" size={12} />
                        {new Date(s.created_at).toLocaleString("en-US", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>
                  </div>

                  {/* RIGHT */}
                  <Button
                    size="sm"
                    className="w-full sm:w-auto"
                    iconName="RotateCcw"
                    onClick={() =>
                      navigate(location.pathname, {
                        state: {
                          autoSearch: true,
                          query: s.query,
                          offerType: s.offer_type,
                          payload: s.payload,
                        },
                        replace: true,
                      })
                    }
                  >
                    Repeat
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <SEO
        title="Caută oferte de călătorie"
        description="Caută rapid oferte de zboruri, hoteluri și servicii de călătorie. TravelAI te ajută să găsești opțiuni potrivite, mai rapid."
        canonicalPath="/search-offers"
      />
      <div className="min-h-screen bg-slate-50 pt-24 pb-20">

      {/* ================= HEADER + TABS ================= */}
      <div className="bg-white border-b sticky top-0 z-40">

        <div className="max-w-6xl mx-auto px-4 py-6 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900">
              Search offers
            </h1>
            <p className="text-slate-600 mt-2">
              Your engine for finding great vacations.
            </p>
          </div>

          <Button onClick={() => navigate("/ai-chat-interface")}>
            <Icon name="MessageCircle" size={18} />
            Chat AI
          </Button>
        </div>

        {/* ===== MOBILE TABS – FULL WIDTH ===== */}
        <div className="sm:hidden grid grid-cols-3 text-center border-t">
          {[
            { id: "search", label: "Search", icon: "🔍" },
            { id: "favorites", label: "Favorites", icon: "❤️" },
            { id: "searches", label: "Searches", icon: "🕘" },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 text-sm font-medium transition
              ${activeTab === tab.id
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-slate-500"}
            `}
            >
              <div className="flex flex-col items-center gap-0.5">
                <span>{tab.icon}</span>
                <span>
                  {tab.label}
                  {tab.id === "favorites" && ` (${favorites.length})`}
                  {tab.id === "searches" && ` (${savedSearches.length})`}
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* ===== DESKTOP TABS ===== */}
        <div className="hidden sm:flex gap-6 max-w-6xl mx-auto px-4 pt-4">
          <button
            onClick={() => setActiveTab("search")}
            className={`pb-2 ${activeTab === "search"
              ? "font-bold text-blue-600 border-b-2 border-blue-600"
              : "text-slate-500 hover:text-slate-800"
              }`}
          >
            🔍 Search
          </button>

          <button
            onClick={() => setActiveTab("favorites")}
            className={`pb-2 ${activeTab === "favorites"
              ? "font-bold text-blue-600 border-b-2 border-blue-600"
              : "text-slate-500 hover:text-slate-800"
              }`}
          >
            ❤️ Favorites ({favorites.length})
          </button>

          <button
            onClick={() => setActiveTab("searches")}

            className={`pb-2 ${activeTab === "searches"
              ? "font-bold text-blue-600 border-b-2 border-blue-600"
              : "text-slate-500 hover:text-slate-800"
              }`}
          >
            🕘 Searches ({savedSearches.length})
          </button>
        </div>
      </div>

      {/* ================= PAGE CONTENT ================= */}
      <div className="max-w-6xl mx-auto px-4">

        <div className="border-b mb-8" />
        <div ref={searchTopRef} />

        {/* SEARCH FORM – rămâne montat */}
        <div style={{ display: activeTab === "search" ? "block" : "none" }}>
          <SearchOffers
            onSearch={handleSearch}
            initialFormData={initialFormData}
            initialOfferType={initialOfferType}
          />
        </div>


        <div ref={resultsRef} />

        {/* CONTENT */}
        <div className="mt-10">{renderContent()}</div>
      </div>
      </div>
    </>
  );

};

export default OffersPage;
